import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { MRIMachinePopulated, MachineFavorite } from '../../room-overview/types';
import {
    getMachineCompatibility,
    MachineCompatibilityResponseDTO,
} from '../../../services/api/mriMachineCompatibilityService';
import { getMachineFavorites } from '../../../services/api/mriMachineFavouriteService';
import { getMRIMachineById } from '../../../services/apiService';
import CompatibilityResults from './CompatibilityResults';
import CompatibilityMachineDetail from './CompatibilityMachineDetail';

interface MRICompatibilityDialogProps {
    conditionalId: string | null;
}

export default function MRICompatibilityDialog({
    conditionalId,
}: MRICompatibilityDialogProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<MachineCompatibilityResponseDTO | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [selectedMachineId, setSelectedMachineId] = useState<string | null>(null);
    const [detailMachine, setDetailMachine] = useState<MRIMachinePopulated | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const [favoritedMachineIds, setFavoritedMachineIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!open || !conditionalId) return;

        let cancelled = false;

        setLoading(true);
        setError(null);
        setData(null);
        setSelectedMachineId(null);
        setDetailMachine(null);

        const fetchData = async () => {
            try {
                const [compatibilityData, favoritesData] = await Promise.all([
                    getMachineCompatibility(conditionalId),
                    getMachineFavorites().catch((): MachineFavorite[] => []),
                ]);
                if (cancelled) return;
                setData(compatibilityData);
                setFavoritedMachineIds(
                    new Set(favoritesData?.map((f: MachineFavorite) => f.machine?._id).filter(Boolean) || [])
                );
            } catch {
                if (!cancelled) setError(t('mriCompatibility.errors.loadFailed'));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();

        return () => { cancelled = true; };
    }, [open, conditionalId, t]);

    const handleFavoriteChange = (machineId: string, isFavorited: boolean) => {
        setFavoritedMachineIds((prev) => {
            const next = new Set(prev);
            if (isFavorited) {
                next.add(machineId);
            } else {
                next.delete(machineId);
            }
            return next;
        });
    };

    const handleCardClick = async (machineId: string) => {
        setSelectedMachineId(machineId);
        setDetailLoading(true);
        try {
            const machine = await getMRIMachineById(machineId);
            setDetailMachine(machine);
        } catch {
            setError(t('mriCompatibility.errors.detailLoadFailed'));
            setSelectedMachineId(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleBack = () => {
        setSelectedMachineId(null);
        setDetailMachine(null);
        setError(null);
    };

    const selectedResult = data?.results.find(
        (r) => r.machine._id === selectedMachineId
    );

    return (
        <>
            <Button variant="outline" onClick={() => setOpen(true)}>
                <ShieldCheck className="h-4 w-4 mr-2" />
                {t('mriCompatibility.title')}
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent aria-describedby={undefined} className="max-w-6xl h-[85vh] overflow-y-auto content-start [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    <DialogHeader className="space-y-1 pb-2">
                        <DialogTitle className="text-xl">{t('mriCompatibility.title')}</DialogTitle>
                        {data && data.results.length > 0 && !selectedMachineId && (
                            <DialogDescription>
                                {t('mriCompatibility.description', { count: data.results.length })}
                            </DialogDescription>
                        )}
                    </DialogHeader>
                    {selectedMachineId ? (
                        <CompatibilityMachineDetail
                            loading={detailLoading}
                            machine={detailMachine}
                            compatibilityResult={selectedResult}
                            implantCoilWarning={data?.implantCoilWarning ?? null}
                            onBack={handleBack}
                        />
                    ) : (
                        <CompatibilityResults
                            key={conditionalId}
                            loading={loading}
                            error={error}
                            conditionalId={conditionalId}
                            data={data}
                            favoritedMachineIds={favoritedMachineIds}
                            onFavoriteChange={handleFavoriteChange}
                            onCardClick={handleCardClick}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
